import {
  MenuItemProps,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';
import { StandardSnack, useSnackbar } from '@components/snackbar';
import { css, Global } from '@emotion/react';
import { Container } from '@sketch-note/design-system/src/components/container';
import { Dialog } from '@sketch-note/design-system/src/components/dialogs';
import { ShareComponent } from '@sketch-note/design-system/src/components/share';
import { CloseIcon, PrivateIcon, SearchIcon, SharedIcon, WarningIcon } from '@sketchnote/icons';
import {
  FilterLabel,
  MoreActionMenu,
  MoreActionMenuProps,
  SortActiveText,
  WorkspaceNoOptionsMessage,
  WorkspaceSelectDropDownIndicator,
  WorkspaceSelectOption,
  WorkspaceSelectStyles,
} from '@sketchnote/shared-components';
import { ViewFile } from '@sketchnote/shared-components';
import { useRouter } from 'next/router';
import * as React from 'react';
import Select from 'react-select';
import { API, useSocketSyncOrg } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { useWorkspaceService } from 'src/api/services/workspaces/workspace';
import { useLoaderStore } from 'src/components/loaders';
import { useTimerWidgetStore } from 'src/components/navbar/TimerWidget/useTimerWidgetStore';
import { useSidebarStore } from 'src/components/sidebar/store';
import { withAuth } from 'src/hoc';
import { OverviewLayout } from 'src/layouts/overview';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useDebounce } from 'use-debounce';

import { useFiles, useFilesStore } from '../api/getFiles';
import {
  filterOptions,
  menuItemsAll,
  menuItemsArchive,
  menuItemsDelete,
  sortOptions,
  tabsData,
  viewOptions,
} from '../assets/data';
import { FilesView } from '../components/FilesView';
const { useState, useCallback, useMemo, useEffect } = React;

const PAGE_SIZE = 10;

export interface FileListProps {
  name: string;
  modified: {
    author: string;
    timing: string;
  };
  onClick?: ({
    fileId,
    viewId,
    type,
    view,
  }: {
    fileId: string;
    viewId: string;
    type: string;
    view?: string;
  }) => void | undefined;
  sharedWith?: any[];
  menuItems?: { label: string; value: string; icon?: JSX.Element }[];
  onClickMenuItem?: (
    menuitem: MenuItemProps,
    e: React.KeyboardEvent<HTMLDivElement>,
    id: string,
    name: string
  ) => void;
  className?: string;
  id: string;
  icon?: any;
  onPinFile?: (id: string, pin: boolean, name: string) => void;
  onFavoriteFile?: (id: string, favorite: boolean, name: string) => void;
  pinned?: boolean;
  favorite?: boolean;
  type: string;
  view: string;
  avatarMaxCount?: number;
  primaryViewId: string;
  showMenu?: boolean;
  titleClassName?: string;
  showWorkspaceDetails?: boolean;
  workspace?: { icon?: any; id: string; name: string };
}

export const FilesPage = withAuth(() => {
  const router = useRouter();
  const [isShareOpened, setIsShareOpened] = useState(false);
  const [currentMode, setCurrentMode] = useState(router?.query?.list as string);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortValue, setSortValue] = useState(sortOptions[0]);
  const [viewValue, setViewValue] = useState(viewOptions[0]);
  const [filterValue, setFilterValue] = useState(filterOptions[0]);
  const [fileInfo, setFilesInfo] = useState<{ id: string; name: string }>({ id: '', name: '' });

  const [filesUpdatedData, setFilesUpdatedData] = useState<any[]>([]);
  const [isDeleteFilesPermanentlyModalOpen, setisDeleteFilesPermanentlyModalOpen] = useState(false);
  const [isDeleteFilesModalOpen, setisDeleteFilesModalOpen] = useState(false);

  const { setIsLoading } = useLoaderStore();

  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [isCopyDocOpen, setIsCopyDocModalOpen] = useState(false);
  const {
    toggleShared,
    togglePrivate,
    toggleWorkspace,
    toggleWorkspaceById,
    workspaces: sidebarWorkspaces,
    isSharedOpen,
    isPrivateOpen,
    isWorkspaceOpen,
  } = useSidebarStore((s) => ({
    toggleShared: s.toggleShared,
    togglePrivate: s.togglePrivate,
    toggleWorkspace: s.toggleWorkspace,
    toggleWorkspaceById: s.toggleWorkspaceById,
    isSharedOpen: s.isSharedOpen,
    isPrivateOpen: s.isPrivateOpen,
    isWorkspaceOpen: s.isWorkspaceOpen,
    workspaces: s.workspaces,
  }));
  const [copyDocName, setCopyDocName] = useState('');
  const [isMoveOpen, setIsMoveModalOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [onSearchFocus, setOnSearchFocus] = useState<boolean>(false);
  const [debouncedSearchKeyword] = useDebounce(searchKeyword, 400);

  const [moveWorkspaceId, setMoveWorkspaceId] = useState('');
  const { useWorkspaces } = useWorkspaceService();
  const [openSnackbar] = useSnackbar();
  const { userWorkspaces: workspaces, revalidate: workspaceRevalidate } = useWorkspaces({});

  const { actions, data: filesData, isLoading, total } = useFilesStore();
  const [filesRevalidate, setFilesRevalidate] = useState(true);

  const { fetchTimers } = useTimerWidgetStore((s) => ({
    fetchTimers: s.fetchTimers,
  }));

  const otherWorkspaces = useMemo(
    () => [
      {
        label: 'Workspaces',
        options: workspaces
          .map(
            (obj) =>
              obj.workspaceId !== workspaceId &&
              obj.name !== 'default-workspace' && {
                label: obj.name,
                value: obj.workspaceId,
              }
          )
          .filter((x) => x),
      },
    ],
    [router, workspaces]
  );
  const handleProgressPagination = (page: number) => {
    setCurrentPage(page - 1);
  };

  const handleDeleteFilesPermanently = async () => {
    setIsLoading(true);
    setisDeleteFilesPermanentlyModalOpen(false);
    try {
      const response = await API.delete(`/files?id=${fileInfo.id}`);
      if (response.data) {
        setFilesRevalidate(!filesRevalidate);
        setIsLoading(false);
        fetchTimers();
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };
  const handleDeleteDocs = async () => {
    setIsLoading(true);
    setisDeleteFilesModalOpen(false);
    try {
      const response = await API.put('/files', { id: fileInfo.id, status: 'deleted' });
      if (response.data) {
        setFilesRevalidate(!filesRevalidate);
        setIsLoading(false);
        openSnackbar({
          text: `${fileInfo.name || 'File'} has been deleted`,
          snackType: 'standard',
          position: 'bottom-center',
        });
        fetchTimers();
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (router?.query?.list || workspaceId) {
      actions.fetchFiles({
        filter: filterValue.value,
        itemLimit: 10,
        mode: router?.query?.list as string,
        pageNum: currentPage + 1,
        sortValue: sortValue.value,
        workspace: workspaceId,
        search: debouncedSearchKeyword,
      });
    }
  }, [filesRevalidate, currentPage, workspaceId, router?.query?.list, debouncedSearchKeyword]);

  const [timer, setTimer] = useState(true);

  useEffect(() => {
    if (router.query.list) {
      toggleShared(router.query.list === 'shared');
      togglePrivate(router.query.list === 'private');
    } else if (router.query.workspaceId) {
      toggleWorkspace(true);
      toggleWorkspaceById(true, router.query.workspaceId as string);
    }
    setSearchKeyword('');
  }, [router.query, sidebarWorkspaces]);

  useEffect(() => {
    setWorkspaceId(router.query.workspaceId ? (router.query.workspaceId as string) : '');
  }, [router]);

  useEffect(() => {
    if (filesData?.length > 0) {
      setFilesUpdatedData(filesData);
    } else {
      setFilesUpdatedData([]);
    }
  }, [JSON.stringify(filesData)]);

  const handleShareClose = useCallback(() => {
    setIsShareOpened(false);
  }, []);

  const handleSortChange = (value) => {
    setSortValue(value);

    setFilesRevalidate(!filesRevalidate);
  };
  const handleViewChange = (value) => {
    setViewValue(value);

    setFilesRevalidate(!filesRevalidate);
  };
  const handleFilterChange = (value) => {
    setFilterValue(value);
    setCurrentPage(0);

    setFilesRevalidate(!filesRevalidate);
  };

  const handleMenuItemClick = useCallback(
    async (item, e, id, name) => {
      setFilesInfo({ id: id, name: name });
      e.stopPropagation();

      if (item.value === 'delete-permanently') {
        setisDeleteFilesPermanentlyModalOpen(true);
      } else if (item.value === 'move') setIsMoveModalOpen(true);
      else if (item.value === 'copy') setIsCopyDocModalOpen(true);
      else if (item.value === 'deleted') setisDeleteFilesModalOpen(true);
      else {
        setIsLoading(true);
        const response = await API.put('/files', { id: id, status: item.value });
        if (response.data) {
          setFilesRevalidate(!filesRevalidate);
          setIsLoading(false);
        }
        openSnackbar({
          text: `${fileInfo.name || 'File'} has been archived`,
          snackType: 'standard',
          position: 'bottom-center',
        });
      }
    },
    [filesRevalidate]
  );

  const authData = useAuth((s) => s.userDetails);
  const socketSyncOrg = useSocketSyncOrg({
    organisation: authData?.memberInformation?.organisation?.organisationId,
  });

  useEffect(() => {
    setFilesRevalidate(!filesRevalidate);
  }, [socketSyncOrg]);

  const handleMoveFile = async () => {
    const payload = { id: fileInfo.id, workspace: moveWorkspaceId };
    setIsLoading(true);
    const response = await API.put('/files', payload);
    if (response.data) {
      setFilesRevalidate(!filesRevalidate);
      await workspaceRevalidate();

      const destinationWorkspace = workspaces.find((w) => w.workspaceId === moveWorkspaceId);
      if (moveWorkspaceId && destinationWorkspace)
        Analytics.track('File moved', {
          'Destination workspace name': destinationWorkspace.name,
          'Destination workspace id': moveWorkspaceId,
        });
      setIsMoveModalOpen(false);
      setIsLoading(false);

      setFilesRevalidate(!filesRevalidate);
    }
  };

  const handleCopyDoc = async () => {
    setIsLoading(true);
    const response = await API.get(`/files/duplicate?id=${fileInfo.id}&title=${copyDocName}`);

    if (response.data) {
      setFilesRevalidate(!filesRevalidate);
      await workspaceRevalidate();

      openSnackbar({
        text: `${
          router?.query?.list === 'shared'
            ? `${copyDocName || 'File'} has been created in your private files`
            : `${copyDocName || 'File'} has been created`
        }`,
        snackType: 'standard',
        position: 'bottom-center',
      });

      setIsCopyDocModalOpen(false);
      setIsLoading(false);
    }
  };

  const handleSelectMoveFile = (value) => {
    setMoveWorkspaceId(value.value);
  };

  const onEnterSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  const onClearSearch = () => {
    setSearchKeyword('');
  };

  return (
    <OverviewLayout>
      <Global
        styles={css`
          body {
            overflow: hidden;
          }
          .page-container > div {
            height: 100%;
          }
        `}
      />
      <div className="overflow-auto h-full">
        <div className="bg-gray-100 min-h-full custome-scrollbar-1 overflow-auto py-38">
          <Container className="small:px-16">
            <div className="">
              {router.query.workspaceId ? (
                <h3 className="text-black flex text-2xl font-semibold">Files</h3>
              ) : (
                <div></div>
              )}
              <div className="flex-col border-b pb-16">
                <div className="flex justify-between mb-1 ">
                  <div>
                    {router?.query?.list === 'shared' ? (
                      <div className="text-black flex text-2xl font-bold">
                        <span className="mt-6 inline-block">
                          <SharedIcon size={24} />
                        </span>
                        <span>&nbsp;Shared</span>
                      </div>
                    ) : router?.query?.list === 'private' ? (
                      <div className="text-black text-2xl font-bold flex">
                        <span className="mt-5 inline-block">
                          <PrivateIcon size={24} />
                        </span>
                        <span>&nbsp;Private</span>
                      </div>
                    ) : (
                      <div className="text-gray-500 mt-10 font-medium">
                        Visible to all workspace members
                      </div>
                    )}
                  </div>
                  <div className="text-grey-999 flex  pt-10">
                    <MoreActionMenu
                      menuItems={filterOptions as MoreActionMenuProps['menuItems']}
                      icon={<FilterLabel label={filterValue.label} isFilterOpened={false} />}
                      classNames={{ menuItem: 'hover:text-blue-500' }}
                      onClickMenuItem={handleFilterChange}
                    />
                    <span className="mr-24" />
                    <MoreActionMenu
                      menuItems={sortOptions as MoreActionMenuProps['menuItems']}
                      icon={
                        <SortActiveText
                          sortTxt={'Sort'}
                          activeSortValue={sortValue}
                          isSortMenuOpened={false}
                        />
                      }
                      onClickMenuItem={handleSortChange as MoreActionMenuProps['onClickMenuItem']}
                    />
                    <span className="mr-24" />
                    <MoreActionMenu
                      menuItems={viewOptions as MoreActionMenuProps['menuItems']}
                      icon={
                        <ViewFile
                          isViewMenuOpened={false}
                          viewText={'View'}
                          activeViewValue={viewValue}
                        />
                      }
                      onClickMenuItem={handleViewChange as MoreActionMenuProps['onClickMenuItem']}
                    />
                    <span className="mr-24" />
                    <div className=" bg-gray-150 w-164 rounded-sm flex items-center py-4 px-12 text-gray-600 overflow-hidden">
                      <div
                        className="w-full flex relative"
                        style={{
                          transition: 'all 300ms',
                          transform: onSearchFocus ? 'translateX(-26px)' : '',
                        }}
                      >
                        <SearchIcon wrapperClass="text-gray-500 flex items-center mr-8" />
                        <input
                          type="text"
                          className="w-full bg-gray-150 placeholder-gray-500 text-sm"
                          placeholder={onSearchFocus ? 'Search' : 'Search...'}
                          onChange={onEnterSearch}
                          value={searchKeyword}
                          onFocus={() => setOnSearchFocus(true)}
                          onBlur={() => setOnSearchFocus(false)}
                        />
                      </div>
                      {searchKeyword.length > 0 ? (
                        <CloseIcon
                          className="items-end text-gray-500 cursor-pointer"
                          size={10}
                          onClick={onClearSearch}
                        />
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex bg-blue-150 rounded-lg mt-16 items-center px-10 py-5">
                  <WarningIcon size={14} fill={'#1D4ED8'} />
                  <span className="text-xs text-blue-450 pl-6">
                    Shared/Private folders are being phased out by Sketchnote. You will no longer be
                    able to create or move files to this section. Your existing files will continue
                    to remain here
                  </span>
                </div>
              </div>
              <div className="py-32">
                <FilesView
                  files={filesUpdatedData}
                  filterValue={filterValue.value}
                  view={viewValue.value}
                  mode={currentMode as string}
                  menuItems={
                    filterValue.value === 'deleted'
                      ? menuItemsDelete
                      : filterValue.value === 'archived'
                      ? menuItemsArchive
                      : menuItemsAll
                  }
                  onClickMenuItem={handleMenuItemClick}
                  paginationProps={{
                    total: total,
                    pageSize: PAGE_SIZE,
                    current: currentPage + 1,
                    onChange: handleProgressPagination,
                  }}
                  isLoading={isLoading}
                />
              </div>

              <Dialog open={isShareOpened} nested={true} onClose={handleShareClose}>
                <ShareComponent onClose={handleShareClose} />
              </Dialog>
            </div>
          </Container>
        </div>
      </div>

      <Modal
        isOpen={isDeleteFilesPermanentlyModalOpen}
        onClose={() => {
          setisDeleteFilesPermanentlyModalOpen(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="font-semibold mb-16 text-2xl">Delete permanently?</p>
          <p className="mb-32 text-sm text-gray-999">
            This action will delete your template permanently and cannot be undone
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setisDeleteFilesPermanentlyModalOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDeleteFilesPermanently}
            >
              Delete permanently
            </button>
          </div>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isDeleteFilesModalOpen}
        onClose={() => {
          setisDeleteFilesModalOpen(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="font-semibold mb-16 text-2xl">Delete file?</p>
          <p className="mb-32 text-sm text-gray-999">
            Are you sure you want to delete{' '}
            <span className="font-medium">{fileInfo.name || 'Untitled'} </span>?
            <br />
            <br />
            If you change your mind, you can get this file back by filtering for deleted{' '}
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setisDeleteFilesModalOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDeleteDocs}
            >
              Delete
            </button>
          </div>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isMoveOpen}
        onClose={() => {
          setIsMoveModalOpen(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="font-semibold mb-16 text-2xl">Move files</p>
          <p className="mb-24 text-sm text-gray-999">Transfer files between custom workspaces</p>
          <Select
            className={'mb-32'}
            label="Select workspace or dashboard"
            placeholder="Select workspace"
            styles={WorkspaceSelectStyles}
            options={otherWorkspaces}
            onChange={handleSelectMoveFile}
            components={{
              DropdownIndicator: WorkspaceSelectDropDownIndicator,
              IndicatorSeparator: () => null,
              Option: WorkspaceSelectOption,
              NoOptionsMessage: WorkspaceNoOptionsMessage,
            }}
          />
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsMoveModalOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="bg-blue-800 text-white border rounded-sm px-24 py-13"
              onClick={handleMoveFile}
              disabled={moveWorkspaceId.length === 0}
            >
              Move
            </button>
          </div>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isCopyDocOpen}
        onClose={() => {
          setIsCopyDocModalOpen(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="font-semibold mb-16 text-2xl">Name new file</p>
          <div className="mb-32">
            <input
              className="w-full rounded-sm border bg-gray-100 p-13"
              placeholder="Awesome file #1"
              onChange={(e) => setCopyDocName(e.target.value)}
            />
            {router?.query?.list === 'shared' ? (
              <span className="mt-4 text-xs text-gray-500 inline-block">
                * Duplicated Shared files can be found in your Private files
              </span>
            ) : (
              <></>
            )}
          </div>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsCopyDocModalOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="bg-blue-800 text-white border rounded-sm px-24 py-13"
              onClick={handleCopyDoc}
            >
              Create Copy
            </button>
          </div>
        </ModalContent>
      </Modal>
    </OverviewLayout>
  );
});
